import React, { useState, useEffect, ReactText, FC, useMemo } from "react";
import { useQuery } from "react-query";
import { useHistory, useParams, useLocation } from "react-router";
import { Fulfillment } from "@secondcloset/types";
import { SearchInput, Table } from "@secondcloset/web-components";
import { Button, Space, Tag, Typography } from "antd";
import { SorterResult } from "antd/lib/table/interface";
import { get } from "lodash-es";
import { useOrganizationContext } from "contextProviders/organization/OrganizationProvider";
import { fetchProductsIndex } from "../../../api/fulfillment/product";
import { Inventory, fetchInventories } from "api/inventory";
import { getColumns, useMergedProductInventories } from "./helpers";
import { Flex } from "css/styles";
import { FileAddOutlined, FilterFilled, FilterOutlined, UploadOutlined } from "@ant-design/icons";
import { FilterOption, FiltersDrawer } from "components/FilterDrawer";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlagQuery";

type Product = Fulfillment.Product & { inventory: Inventory };
interface QueryOption {
  per_page: number;
  q?: string;
  page?: number;
  shipment_tags?: {
    values: string[];
    condition: string;
  };
  cancelled?: boolean;
  field?: string | number | ReactText[];
  direction?: string;
  organization_id?: string;
  active?: boolean;
  has_errors?: boolean;
  missing_info?: boolean;
  assigned_user_id?: string;
  product_type?: string;
}

const FulfillmentProductIndexPage: FC = () => {
  const history = useHistory();
  const params = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cachedSearch = searchParams.get("search") || "";
  const cachedFilter = searchParams.get("productStatus") || "active";
  const cachedPage = +(searchParams.get("page") || 1);
  const [search, setSearch] = useState(cachedSearch);
  const [page, setPage] = useState(cachedPage);
  const [pageSize, setPageSize] = useState(10);
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<{ field?: string; direction?: string | undefined }>({
    field: "name",
    direction: "",
  });
  const [selectedProductType, setSelectedProductType] = useState<string[]>([]);
  const [selectedProductStatus, setSelectedProductStatus] = useState<string[]>([cachedFilter]);
  const [selectedProductError, setSelectedProductError] = useState<string[]>([]);

  const organizationID = get(params, "organization_id", "");

  const useNewWarehouseInventory = useFeatureFlagQuery(
    "use-new-warehouse-inventory-values",
    { organization_id: organizationID },
    { enabled: !!organizationID }
  );

  const checkEmpty = (name: string, value: React.Key) => {
    if (name === "page" || name === "filter") return true;
    if (name === "search") return !!value;
  };

  const updateSearchParams = (name: string, value: React.Key) => {
    const isValueExisting = checkEmpty(name, value);
    if (isValueExisting) searchParams.set(name, String(value));
    else searchParams.delete(name);
  };

  const updateURL = () => {
    updateSearchParams("page", page);
    updateSearchParams("search", search);
    updateSearchParams("filter", selectedProductStatus.join(`,`) || "all");
    updateSearchParams(sort.field || "", sort.direction || "");
    const url = searchParams.toString();
    history.replace(`?${url}`);
  };

  const queryOptions = useMemo(() => {
    const trimmedSearchInput = search ? search.trim() : "";
    const queryOption: QueryOption = { per_page: pageSize };
    if (organizationID) queryOption.organization_id = organizationID;
    if (trimmedSearchInput) queryOption.q = trimmedSearchInput;
    if (page) queryOption.page = page;
    if (selectedProductType.length === 1) queryOption.product_type = selectedProductType[0];
    if (selectedProductStatus.length === 1 && selectedProductStatus[0] !== "all")
      queryOption.active = selectedProductStatus[0] === "active";
    if (selectedProductError.length === 1 && selectedProductError[0] === "urgent_errors") queryOption.has_errors = true;
    if (selectedProductError.length === 1 && selectedProductError[0] === "missing_info")
      queryOption.missing_info = true;
    if (selectedProductError.length > 1) {
      queryOption.has_errors = true;
      queryOption.missing_info = true;
    }

    if (sort.field && sort.direction) {
      queryOption.field = sort.field;
      queryOption.direction = sort.direction;
    }
    updateURL();
    return queryOption;
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [search, organizationID, page, pageSize, selectedProductStatus, sort, selectedProductType, selectedProductError]);

  const productsQuery = useQuery(["products", queryOptions], () => fetchProductsIndex({ ...queryOptions }), {
    keepPreviousData: true,
  });

  const productIds = productsQuery.data?.data.map((p) => p.id) || [];

  const inventoriesQuery = useQuery(
    ["inventories", productIds, useNewWarehouseInventory.data?.state],
    () =>
      fetchInventories({
        version: useNewWarehouseInventory.data?.state ? 2 : 1,
        organization_id: organizationID || "",
        ids: productIds,
        per_page: productIds.length,
      }),
    { enabled: !!organizationID && !!productIds.length }
  );

  const products = useMergedProductInventories(productsQuery, inventoriesQuery);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goToAddProductPage = () => {
    history.push(`/organizations/${organizationID}/products/create`);
  };
  const goToProductCSVPage = () => {
    history.push(`/organizations/${organizationID}/products/upload-csv`);
  };

  const renderTopButtons = () => {
    if (!organizationID) return null;
    return (
      <Space>
        <Button className="add-product-btn" onClick={goToProductCSVPage} icon={<UploadOutlined />}>
          Upload CSV
        </Button>
        <Button className="add-product-btn" onClick={goToAddProductPage} icon={<FileAddOutlined />}>
          Add Product
        </Button>
      </Space>
    );
  };

  const getSortDirection = (direction?: "ascend" | "descend" | null) => {
    if (direction === "ascend") return "ASC";
    if (direction === "descend") return "DESC";
    return "";
  };

  const handleTableChange = (sorter: SorterResult<Product>) => {
    const { field, order } = sorter;
    setSort({ field: String(field), direction: getSortDirection(order) });
  };

  const buildProductHeader = () => {
    return (
      <div className="order-header">
        <Flex justify="space-between" style={{ width: "100%" }}>
          <Typography.Title level={4}>Products</Typography.Title>
          <div className="customer-specific-options">{renderTopButtons()}</div>
        </Flex>
      </div>
    );
  };

  const SKU_FILTER = [
    { value: "base", label: "Base Product" },
    { value: "kit", label: "Virtual Kit" },
  ];

  const STATUS_FILTER = [
    { value: "active", label: "Active" },
    { value: "archived", label: "Archived" },
  ];

  const ERROR_FILTER = [
    { value: "urgent_errors", label: "Blocking Fulfillment" },
    { value: "missing_info", label: "Missing Information" },
  ];

  const filterOptions = [
    {
      title: "SKU Type",
      value: selectedProductType,
      onChange: setSelectedProductType,
      options: SKU_FILTER,
      clearAll: { allow: true },
      selectAll: { allow: true, label: "All SKU Type" },
    },
    {
      title: "Status",
      value: selectedProductStatus,
      onChange: setSelectedProductStatus,
      options: STATUS_FILTER,
      clearAll: { allow: true },
      selectAll: { allow: true, label: "All Status" },
    },
    {
      title: "Alert",
      value: selectedProductError,
      onChange: setSelectedProductError,
      options: ERROR_FILTER,
      clearAll: { allow: true },
      selectAll: { allow: true, label: "All Alerts" },
    },
  ];

  const handleCloseTag = (value: string) => {
    setSelectedProductType((state) => state.filter((item) => item !== value));
    setSelectedProductStatus((state) => state.filter((item) => item !== value));
    setSelectedProductError((state) => state.filter((item) => item !== value));
  };

  const renderAllFilters = () => {
    return (
      <Flex justify="space-between" style={{ width: "100%", marginBottom: 12 }}>
        <Flex>
          <SearchInput
            placeholder="Search for Products"
            defaultValue={search}
            onSearch={setSearch}
            onClear={() => setSearch("")}
            style={{ width: 250 }}
          />
        </Flex>
        <Flex gap="1em">
          {Object.values({ SKU_FILTER, STATUS_FILTER, ERROR_FILTER })
            .flatMap((o) => o)
            .filter((o) =>
              [...selectedProductType, ...selectedProductStatus, ...selectedProductError].includes(o.value)
            )
            .map((f) => (
              <Tag style={{ borderRadius: 50 }} closable={true} onClose={() => handleCloseTag(f.value)} key={f.value}>
                {f.label}
              </Tag>
            ))}
          <Button type="text" onClick={() => setFiltersOpen(!isFiltersOpen)}>
            {isFiltersOpen ? <FilterFilled /> : <FilterOutlined />}
          </Button>
        </Flex>
      </Flex>
    );
  };

  return (
    <div className="page-container" id="product-index-page">
      <div className="orders">
        {buildProductHeader()}
        {renderAllFilters()}
        <Table
          loading={
            productsQuery.isLoading ||
            productsQuery.isPreviousData ||
            inventoriesQuery.isLoading ||
            useNewWarehouseInventory.isLoading
          }
          dataSource={products}
          rowKey={(p) => p.id}
          columns={getColumns(organizationID, !!useNewWarehouseInventory.data?.state)}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={handleTableChange}
        />
        <FiltersDrawer
          visible={isFiltersOpen}
          onClose={() => setFiltersOpen(false)}
          filterOptions={filterOptions as FilterOption[]}
        />
      </div>
    </div>
  );
};

export default FulfillmentProductIndexPage;
